import { motion } from "framer-motion"
import { EventoDataType, CardAction } from "@/types/events"
import { imagebossSetWidth } from "@/utils/imageboss"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { format, formatDate, isFuture, isPast, parseISO, set } from "date-fns"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { ptBR } from "date-fns/locale"
import { useEffect, useRef, useState } from "react"
import { useIsMobile } from "@/hooks/use-mobile"
import { isInscricaoEncerrada } from "@/utils/date"

interface EventCardV2Props {
  evento: EventoDataType
  cardActions?: CardAction[]
  onCardClick?: () => void
  onSubscribeEvent: () => void
  onSubscribedCounterClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  onAvatarClcik?: (id: string | number) => void
}

export function EventCardV2({
  evento,
  cardActions,
  onCardClick,
  onSubscribeEvent,
  onAvatarClcik,
  onSubscribedCounterClick,
}: EventCardV2Props) {
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [isCardActionsOpened, setIsCardActionsOpened] = useState(false)
  const cardActionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (cardActionRef.current && !cardActionRef.current.contains(event.target as Node)) {
        setIsCardActionsOpened(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [cardActionRef])

  const toggleCardActions = () => {
    setIsCardActionsOpened(!isCardActionsOpened)
  }

  const hasZoom = !!evento.even_link_zoom

  const getFooterText = (dataLimite: string) => {
    if (isInscricaoEncerrada(dataLimite)) {
      return "Inscrições encerradas"
    } else {
      return `Inscrições abertas até ${formatDate(new Date(dataLimite), "dd/MM/yyyy")}`
    }
  }

  const getPeriodText = (startDate: string, endDate: string) => {
    const start = format(parseISO(startDate), "dd")
    const end = format(parseISO(endDate), "dd")
    if (start === end) {
      return `${start} de`
    }

    return `${start} à ${end}`
  }

  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      onClick={() => {
        onCardClick()
      }}
      className={`group relative h-full cursor-pointer overflow-clip rounded-lg border border-border bg-surface/50 p-4 pb-2 backdrop-blur-sm`}
    >
      <div className="flex flex-col items-start gap-2">
        <div className="grid w-full grid-cols-1 sm:gap-4 xl:grid-cols-12">
          <div className="col-span-8">
            <div className="relative left-0 top-0 flex h-[100px] w-full items-center justify-center overflow-hidden rounded-lg bg-surface-elevated">
              {(() => {
                const imageUrl = evento.foto_url || evento.eve_foto_url || evento.ime_foto_url

                return imageUrl ? (
                  <img
                    src={imagebossSetWidth(imageUrl, 200) ?? imageUrl}
                    alt={evento.descricao}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <i className="fi fi-ts-calendar-image text-muted" />
                )
              })()}
              <div className="absolute bottom-0 flex size-full items-end bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center gap-2 px-2 py-1 text-white">
                  <i className="fi fi-ts-marker flex items-center justify-center" />
                  <p className="text-xs">{evento.local}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-start md:col-span-4 md:justify-end">
            <div className="flex w-full items-center justify-center gap-4 sm:justify-end">
              <div>
                <div className="flex justify-center gap-2 text-center text-muted-foreground sm:justify-end">
                  <div className="flex flex-row items-center justify-between gap-2 md:flex-col md:items-end md:justify-end md:gap-0">
                    <div className="text-nowrap text-2xl leading-8">
                      <span>
                        {format(parseISO(evento.data_inicio), "M") !==
                        format(parseISO(evento.data_fim), "M") ? (
                          <>
                            {format(parseISO(evento.data_inicio), "dd MMM", { locale: ptBR })} à{" "}
                            {format(parseISO(evento.data_fim), "dd", { locale: ptBR })}
                          </>
                        ) : (
                          getPeriodText(evento.data_inicio, evento.data_fim)
                        )}
                      </span>
                    </div>

                    <div>
                      <span className="text-[1.2rem] leading-8 2xl:text-[2.7rem]">
                        {format(parseISO(evento.data_fim), "MMM", {
                          locale: ptBR,
                        }).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <i className="fi fi-ts-clock flex items-center" />
                      {format(parseISO(evento.data_inicio), "HH:mm", {
                        locale: ptBR,
                      })}
                      -
                      {format(parseISO(evento.data_fim), "HH:mm", {
                        locale: ptBR,
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1 overflow-hidden">
          <h3 className="text-xl font-semibold text-foreground transition-colors group-hover:text-orange-500">{evento.descricao}</h3>
        </div>
        <div className="flex w-full flex-wrap items-center justify-between gap-4 pt-2">
          <div className="flex items-center gap-2">
            {evento.pessoas_insc.length > 0 ? (
              <div className="flex -space-x-2">
                {evento.pessoas_insc.map((pessoa, index) => (
                  <Tooltip
                    delayDuration={0}
                    disableHoverableContent
                    key={pessoa.pes_id}
                  >
                    <TooltipTrigger>
                      <div
                        key={pessoa.pes_id + index}
                        onClick={(e) => {
                          e.stopPropagation()
                          onAvatarClcik(pessoa.pes_uid ?? pessoa.pes_id)
                        }}
                        className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-surface-elevated bg-accent text-xs transition-transform hover:-translate-y-2"
                      >
                        {pessoa.foto_url === null ? (
                          <i className="fi fi-ts-circle-user flex items-center justify-center text-lg text-white"></i>
                        ) : (
                          <img
                            src={pessoa.foto_url}
                            alt=""
                          />
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <h6 className="font-bold capitalize">{pessoa.pes_nome.toLowerCase()}</h6>
                      <p className="text-xs capitalize italic">
                        {pessoa.emp_fantasia.toLowerCase()}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ))}

                {Number(evento.inscritos) - evento.pessoas_insc.length > 0 && (
                  <Tooltip>
                    <TooltipTrigger>
                      <div
                        onClick={onSubscribedCounterClick}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-surface-elevated bg-accent text-xs transition-transform hover:-translate-y-2"
                      >
                        +{Number(evento.inscritos) - evento.pessoas_insc.length}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Ver todos</TooltipContent>
                  </Tooltip>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted">Nenhum inscrito</div>
            )}
          </div>
          {evento.inscrito && hasZoom ? (
            <Button
              className="w-full bg-gradient-to-tr from-blue-900 to-blue-500 font-bold text-blue-100 shadow-lg transition-all hover:scale-[1.02] hover:shadow-blue-600/40 px-8 2xl:w-fit"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                window.open(evento.even_link_zoom!, "_blank", "noopener,noreferrer")
              }}
            >
              <i className="fi fi-ts-video-camera mr-1 flex items-center justify-center" />
              Entrar
            </Button>
          ) : (
            <Button
              className="w-full px-8 2xl:w-fit"
              variant={
                evento.inscrito ||
                isInscricaoEncerrada(evento.data_limite) ||
                isPast(new Date(evento.data_inicio))
                  ? "secondary"
                  : "default"
              }
              size="sm"
              disabled={
                evento.inscrito ||
                isInscricaoEncerrada(evento.data_limite) ||
                isPast(new Date(evento.data_inicio))
              }
              onClick={(e) => {
                e.stopPropagation()
                onSubscribeEvent()
              }}
            >
              {evento.inscrito ? "Inscrito" : "Inscrever-me"}
            </Button>
          )}
        </div>
      </div>

      <hr className="my-2" />

      <div>
        <div className="text-center text-sm italic text-[#666]">
          {getFooterText(evento.data_limite)}
        </div>
      </div>

      {cardActions && cardActions.length > 0 && !isMobile && (
        <div
          ref={cardActionRef}
          className={`absolute bottom-0 left-0 w-full bg-[#333] transition-all duration-700 group-hover:translate-y-0 ${isCardActionsOpened ? "h-[50%] translate-y-0" : "h-5 translate-y-10"}`}
        >
          <div className="bottom-100 relative -top-[15px] flex w-full justify-center rounded-full">
            <div
              onClick={toggleCardActions}
              className="flex h-10 w-full cursor-pointer items-start justify-center rounded-md bg-[#333]"
            >
              <i
                className={`fi fi-ts-angle-small-up flex items-center justify-center transition-all ${isCardActionsOpened ? "rotate-180" : ""}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,_minmax(30px,_1fr))] gap-4 px-8">
            {cardActions.map((action, index) => (
              <div
                key={`action-${index}`}
                className={`flex flex-col items-center gap-2 text-center ${action.disabled ? "hidden" : ""}`}
              >
                <i
                  onClick={() => action.onAction(evento)}
                  className={`fi cursor-pointer fi-ts-${action.icon} flex size-12 items-center justify-center rounded-full bg-muted-foreground/10 text-2xl transition-colors hover:bg-muted-foreground/20`}
                />
                <span className="text-sm leading-3">{action.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}
